import { useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { clsx } from 'clsx'

interface DrawerItem {
  to: string
  label: string
  tier?: 'free' | 'pro' | 'pro+'
}

const SECTIONS: { title: string; items: DrawerItem[] }[] = [
  {
    title: 'Markets',
    items: [
      { to: '/terminal',      label: 'Terminal' },
      { to: '/multi-chart',   label: 'Multi-Chart',     tier: 'pro' },
      { to: '/screener',      label: 'Screener' },
      { to: '/crypto',        label: 'Crypto' },
      { to: '/watchlist',     label: 'Watchlist' },
    ],
  },
  {
    title: 'Analysis',
    items: [
      { to: '/options',      label: 'Options',          tier: 'pro' },
      { to: '/macro',        label: 'Macro' },
      { to: '/sectors',      label: 'Sectors' },
      { to: '/social',       label: 'Social Sentiment' },
      { to: '/calculators',  label: 'Calculators' },
      { to: '/seasonality',  label: 'Seasonality' },
    ],
  },
  {
    title: 'Research',
    items: [
      { to: '/research', label: 'Research Hub', tier: 'pro' },
    ],
  },
  {
    title: 'Flows & Risk',
    items: [
      { to: '/flows', label: 'Flows Hub', tier: 'pro' },
      { to: '/risk',  label: 'Risk Hub',  tier: 'pro' },
    ],
  },
  {
    title: 'Portfolio',
    items: [
      { to: '/portfolio', label: 'Portfolio Hub' },
    ],
  },
  {
    title: 'Strategy & Bots',
    items: [
      { to: '/bot', label: 'Strategy Ideas (Paper)', tier: 'pro' },
    ],
  },
  {
    title: 'Events',
    items: [
      { to: '/events',   label: 'Events Hub' },
      { to: '/messages', label: 'Messages' },
    ],
  },
  {
    title: 'Account',
    items: [
      { to: '/guide',    label: 'User Guide' },
      { to: '/settings', label: 'Settings' },
      { to: '/admin',    label: 'Admin' },
    ],
  },
]

const TIER_BADGE: Record<string, string> = {
  'pro':  'PRO',
  'pro+': 'PRO+',
}

interface MobileDrawerProps {
  open: boolean
  onClose: () => void
}

export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* drawer panel — slides up from bottom */}
      <div
        className="relative mt-auto w-full max-h-[85vh] bg-bb-surface border-t border-bb-border rounded-t-2xl flex flex-col"
        style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))' }}
      >
        {/* drag handle */}
        <div className="flex justify-center pt-3 pb-2 shrink-0">
          <div className="w-10 h-1 rounded-full bg-bb-border" />
        </div>

        <div className="flex items-center justify-between px-4 pb-3 border-b border-bb-border shrink-0">
          <span className="text-xs font-semibold text-bb-subtle uppercase tracking-wider">All Pages</span>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-bb-border/60 text-bb-subtle hover:text-bb-text"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-5">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <p className="text-2xs font-semibold text-bb-disabled uppercase tracking-widest mb-2">
                {section.title}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium border transition-colors',
                        isActive
                          ? 'bg-bb-orange/15 border-bb-orange/40 text-bb-orange'
                          : 'bg-bb-surface-2 border-bb-border/50 text-bb-subtle hover:text-bb-text hover:bg-bb-surface-3'
                      )
                    }
                  >
                    <span className="truncate">{item.label}</span>
                    {item.tier && (
                      <span className="ml-1 text-2xs text-bb-orange/70 font-bold shrink-0">
                        {TIER_BADGE[item.tier]}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
